import { BadgeProps } from '@/components/badge';
import Card, { CardContent, CardHeader } from '@/components/card';
import DataTable, {
  DataTableBadgeCell
} from '@/components/data-table/DataTable.component';
import {
  ProgramChange,
  ProgramService
} from '@/services/ops/investments/Program.service';
import DOMInteraction from '@/utils/DOMInteraction';
import formatters from '@/utils/Formatters';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { useQuery } from '@tanstack/react-query';

import { ColDef } from 'ag-grid-community';
import { isAfter, isBefore, isEqual, startOfDay, startOfToday } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';

import { ProgramChangesDetailCellRenderer } from './ProgramChangesDetailCellRenderer.component';

const getStatusColor = (status: ProgramChange['status']): BadgeProps['color'] =>
  ({
    approved: 'success',
    calceled: 'neutral',
    pending: 'warning',
    rejected: 'warning'
  })[status] as BadgeProps['color'];

const getStatusLabel = (status: ProgramChange['status']): string =>
  ({
    approved: 'Approved',
    canceled: 'Canceled',
    pending: 'Pending Approval',
    rejected: 'Rejected'
  })[status];

const columnDefs: ColDef<ProgramChange>[] = [
  {
    cellRenderer: () => <></>,
    field: 'effectiveDate',
    headerName: '',
    maxWidth: 55,
    minWidth: 55
  },
  {
    field: 'effectiveDate',
    flex: 1,
    headerName: 'Effective Date',
    minWidth: 200,
    valueFormatter: ({ value }) =>
      formatters.formatFromIsoDateCustom(value, 'MM/DD/YYYY')
  },
  {
    cellRenderer: ({ value }: { value: ProgramChange['status'] }) => (
      <DataTableBadgeCell
        color={getStatusColor(value)}
        data-testid={`status-${value}`}>
        {getStatusLabel(value)}
      </DataTableBadgeCell>
    ),
    field: 'status',
    flex: 1,
    headerName: 'Status',
    minWidth: 200
  },
  {
    cellRenderer: () => 'Add Plan',
    field: 'effectiveDate',
    flex: 2,
    headerName: 'Type',
    minWidth: 200
  },
  {
    field: 'sponsorPlanName',
    flex: 3,
    headerName: 'Plan',
    minWidth: 400
  }
];

type ProgramChangesProps = {
  programId: number;
};

type Tabs = 'pending' | 'completed';

export const ProgramChanges: FC<ProgramChangesProps> = props => {
  const [activeTab, setActiveTab] = useState<Tabs>('pending');

  const detailCellRenderer = useCallback(
    cellParams => <ProgramChangesDetailCellRenderer cellParams={cellParams} />,
    []
  );

  const programChangesWithApprovals = useQuery(
    ['ProgramService.getProgramChangesWithApprovals', props.programId],
    () => ProgramService.getProgramChangesWithApprovals(props.programId),
    {
      select: res =>
        res.changes.reduce(
          (acc, curr) => ({
            completed: [
              ...acc.completed,
              ...(curr.status !== 'pending' &&
              (curr.status !== 'approved' ||
                isBefore(startOfDay(curr.effectiveDate), startOfToday()) ||
                isEqual(startOfDay(curr.effectiveDate), startOfToday()))
                ? [curr]
                : [])
            ],
            pending: [
              ...acc.pending,
              ...(curr.status === 'pending' ||
              (curr.status === 'approved' &&
                isAfter(startOfDay(curr.effectiveDate), startOfToday()))
                ? [curr]
                : [])
            ]
          }),
          {
            completed: [],
            pending: []
          }
        ) as {
          completed: ProgramChange[];
          pending: ProgramChange[];
        },
      staleTime: Infinity
    }
  );

  const toggles = useMemo(
    () => [
      {
        onChangeAction: (_, newTab: Tabs) => setActiveTab(newTab),
        options: [
          {
            ...(programChangesWithApprovals.data?.pending?.length
              ? {
                  BadgeProps: {
                    badgeContent:
                      programChangesWithApprovals.data?.pending?.length
                  }
                }
              : {}),
            label: 'Pending',
            value: 'pending'
          },
          {
            ...(programChangesWithApprovals.data?.completed?.length
              ? {
                  BadgeProps: {
                    badgeContent:
                      programChangesWithApprovals.data?.completed?.length
                  }
                }
              : {}),
            label: 'Completed',
            value: 'completed'
          }
        ],
        value: 'pending'
      }
    ],
    [programChangesWithApprovals.data]
  );

  return (
    <Card>
      <CardHeader
        loading={programChangesWithApprovals.isFetching}
        title='Program Changes'
        toggles={toggles}
      />

      <CardContent
        disablePadding
        loading={programChangesWithApprovals.isFetching}>
        <DataTable
          columnDefs={columnDefs}
          columnSizing='fit'
          defaultColDef={{
            editable: false,
            suppressMenu: true,
            suppressMovable: true
          }}
          detailCellRenderer={detailCellRenderer}
          detailColumn={0}
          detailRowAutoHeight
          icons={{
            groupContracted: DOMInteraction.JSXToHTML(
              <ArrowRightIcon
                className='mui-group-icon'
                color='disabled'
                data-testid='group-icon-collapsed'
                fontSize='small'
              />
            ),
            groupExpanded: DOMInteraction.JSXToHTML(
              <ArrowDropDownIcon
                className='mui-group-icon'
                color='disabled'
                data-testid='group-icon-expanded'
                fontSize='small'
              />
            )
          }}
          pagination
          paginationPosition='bottom'
          rowData={
            (activeTab === 'pending'
              ? programChangesWithApprovals.data?.pending
              : programChangesWithApprovals.data?.completed) ?? []
          }
          rowHeight={56}
        />
      </CardContent>
    </Card>
  );
};
